import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { getRentalPortalApplicantsPage } from '../../../../../api'
import { EMPTY_LIST } from '../../../../../constants/common'
import { RENTAL_APPLICANT_ENTITY } from '../../../../../constants/entity'
import { useSelection, useTimeZone } from '../../../../../hooks'
import { isListViewTypeBoard } from '../../../../../utils/listViewType'
import { useLocationPagination } from '../../../../Pagination'
import { APPLICANT_PAYLOAD_FIELD } from '../../../../RentalPortalApplicant'
import {
  APPLICANT_REQUIRED_COLUMNS,
  APPLICANTS_PAGE_SIZE,
} from '../../RentalPortalApplicants.constants'
import {
  getApplicantsColumns,
  getApplicantsPayloadColumns,
  getHasApplicantsMatchScore,
  getHasApplicantsPayloadField,
} from '../../RentalPortalApplicants.utils'
import { APPLICANTS_VIEW_PARAM } from '../useRentalPortalApplicantsViewRegime'

import { EMPTY_ITEMS } from './useRentalPortalApplicantsTable.constants'
import {
  getApplicantsApiBody,
  getApplicantsBoardApiBodies,
  getApplicantsFiltersKey,
  getApplicantsFilterValuesFromKey,
  toApplicantsBoardResponse,
  toApplicantsTableResponse,
} from './useRentalPortalApplicantsTable.utils'

export function useRentalPortalApplicantsTable({ filterValues }) {
  const [searchParams] = useSearchParams()

  const isBoard = isListViewTypeBoard(searchParams.get(APPLICANTS_VIEW_PARAM))

  const timezone = useTimeZone()

  const [columns, setColumns] = useState(getApplicantsColumns)
  const [ordering, setOrdering] = useState(EMPTY_LIST)
  const [pageSize, setPageSize] = useState(APPLICANTS_PAGE_SIZE)
  const [isConfigOpen, setConfigOpen] = useState(false)

  const [reloadKey, setReloadKey] = useState(0)

  const [response, setResponse] = useState(null)
  const [serverError, setServerError] = useState(null)

  const [isInitiated, setInitiated] = useState(false)
  const [isLoadingPage, setLoadingPage] = useState(true)

  const [preview, setPreview] = useState(null)

  const readIdRef = useRef(0)

  const prevReadRef = useRef(null)

  const pageScrollWrapperRef = useRef(null)

  const { pagination: locationPagination, setPagination } =
    useLocationPagination()

  const page = locationPagination.page

  const filtersKey = getApplicantsFiltersKey(filterValues)

  const [prevFiltersKey, setPrevFiltersKey] = useState(filtersKey)

  const items = response?.items ?? EMPTY_ITEMS

  const hasAttention = getHasApplicantsPayloadField({
    field: APPLICANT_PAYLOAD_FIELD.attention,
    items: items.items,
  })

  const hasMatch = getHasApplicantsPayloadField({
    field: APPLICANT_PAYLOAD_FIELD.match,
    items: items.items,
  })

  const hasFitScore = getHasApplicantsMatchScore(items.items)

  const hasStage = getHasApplicantsPayloadField({
    field: APPLICANT_PAYLOAD_FIELD.stage,
    items: items.items,
  })

  const pagination = response?.pagination ?? {
    count: 0,
    page,
    pageCount: 1,
    pageSize,
  }

  const selection = useSelection({ data: items, pagination })

  if (filtersKey !== prevFiltersKey) {
    setPrevFiltersKey(filtersKey)
    selection.handleCleanSelection()
  }

  useEffect(() => {
    readIdRef.current += 1

    const readId = readIdRef.current

    const prevRead = prevReadRef.current

    const isPageTurn =
      prevRead !== null &&
      prevRead.filtersKey === filtersKey &&
      prevRead.ordering === ordering &&
      prevRead.reloadKey === reloadKey &&
      (prevRead.page !== page || prevRead.pageSize !== pageSize)

    prevReadRef.current = { filtersKey, ordering, page, pageSize, reloadKey }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- the in-flight flag flips when the read starts
    setLoadingPage(true)

    const readFilterValues = getApplicantsFilterValuesFromKey(filtersKey)

    const read = isBoard
      ? Promise.all(
          getApplicantsBoardApiBodies({
            filterValues: readFilterValues,
            ordering,
            timezone,
          }).map(({ body, stage }) =>
            getRentalPortalApplicantsPage(body).then(response => ({
              response,
              stage,
            }))
          )
        ).then(toApplicantsBoardResponse)
      : getRentalPortalApplicantsPage(
          getApplicantsApiBody({
            filterValues: readFilterValues,
            ordering,
            page,
            pageSize,
            timezone,
          })
        ).then(toApplicantsTableResponse)

    read
      .then(nextResponse => {
        if (readIdRef.current === readId) {
          setResponse(nextResponse)
          setServerError(null)
          setPagination(nextResponse.pagination, { replace: true })

          if (isPageTurn) {
            pageScrollWrapperRef.current?.scrollTo({
              behavior: 'smooth',
              top: 0,
            })
          }
        }
      })
      .catch(error => {
        if (readIdRef.current === readId) {
          setServerError(error)
        }
      })
      .finally(() => {
        if (readIdRef.current === readId) {
          setLoadingPage(false)
          setInitiated(true)
        }
      })
  }, [filtersKey, isBoard, ordering, page, pageSize, reloadKey, timezone])

  async function handleSetOrdering(nextOrdering) {
    setOrdering(nextOrdering)
    setPagination({ ...locationPagination, page: 1 })

    return nextOrdering
  }

  async function handleResetOrdering() {
    return handleSetOrdering(EMPTY_LIST)
  }

  function handleSetSize(nextSize) {
    setPageSize(nextSize)
    setPagination({ ...locationPagination, page: 1 })
  }

  async function handleRefresh() {
    selection.handleCleanSelection()
    setReloadKey(prevReloadKey => prevReloadKey + 1)
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  async function handleApply() {}

  async function handleUpdateConfig() {}

  return {
    ...selection,
    allItems: response?.allItems ?? EMPTY_LIST,
    columns: getApplicantsPayloadColumns({
      columns,
      hasAttention,
      hasFitScore,
      hasMatch,
    }),
    entity: RENTAL_APPLICANT_ENTITY,
    filterValues,
    handleApply,
    handleConfigOpen,
    handleRefresh,
    handleResetOrdering,
    handleSetColumns: setColumns,
    handleSetOrdering,
    handleSetPagination: setPagination,
    handleSetPreview: setPreview,
    handleSetSize,
    handleUpdateConfig,
    hasAttention,
    hasMatch,
    hasStage,
    isConfigOpen,
    isLoading: !isInitiated,
    isRefetching: isLoadingPage && isInitiated,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions: items.permissions,
    preview,
    requiredColumns: APPLICANT_REQUIRED_COLUMNS,
    serverError,
    setConfigOpen,
    stageTotals: response?.stageTotals,
  }
}
